import { metrics } from '@geo-admin/shared/contract/api/index.ts';
import { EngineTabs, engineLabel, useEngine } from '../components/EngineTabs.tsx';
import type { RouteResponse } from '@geo-admin/shared/contract/route.ts';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { DataTable } from '../components/data-table.tsx';
import { RateCell } from '../components/RateCell.tsx';
import { Badge, Card } from '../components/ui/card.tsx';
import { Select } from '../components/ui/form.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi } from '../lib/api.ts';
import { PageTitle } from '../components/PageTitle.tsx';
import { BrandKindTabs, useBrandKind } from '../components/BrandKindTabs.tsx';

type Domain = RouteResponse<typeof metrics.citations>['domains'][number];

export function CitationsPage() {
  const [days, setDays] = useState(7);
  // 「실제 | 테스트」 탭 — 테스트 브랜드 질문의 샘플을 실제 순위에 섞지 않는다(261023-test-brand)
  const brandKind = useBrandKind();
  const engine = useEngine();
  const res = useApi(metrics.citations, { query: { days, brandKind, engine } });
  const columns: ColumnDef<Domain, unknown>[] = [
    {
      header: '도메인',
      accessorKey: 'host',
      cell: ({ row }) => (
        <span className="flex items-center gap-1.5">
          {row.original.host}
          {row.original.siteUrlPrefix ? <Badge tone="green">우리 사이트</Badge> : null}
        </span>
      ),
    },
    { header: '인용된 샘플', accessorKey: 'samples' },
    {
      header: '비율 (전체 적격 샘플 대비)',
      id: 'share',
      accessorFn: (r) => r.samples,
      cell: ({ row }) => <RateCell rate={row.original.share} />,
    },
  ];
  return (
    <>
      <PageTitle
        title={`인용 도메인 순위 — ${engineLabel(engine)}`}
        description={`${brandKind === 'test' ? '테스트 브랜드' : '모든 고객'}의 적격 샘플 ${res.data?.eligible ?? '…'}건 기준 — 운영자 전용입니다 (다른 고객의 데이터가 섞입니다)`}
        actions={
          <Select className="w-28" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {[7, 14, 30, 90].map((d) => (
              <option key={d} value={d}>
                {d}일
              </option>
            ))}
          </Select>
        }
      />
      <div className="flex flex-wrap items-center gap-4">
        <EngineTabs />
        <span className="h-5 w-px bg-slate-200" aria-hidden />
        <BrandKindTabs />
      </div>
      <Card>
        {res.isLoading ? (
          <Loading />
        ) : res.error ? (
          <ErrorBox error={res.error} />
        ) : (
          <DataTable columns={columns} data={res.data?.domains ?? []} empty="인용이 없습니다" />
        )}
      </Card>
    </>
  );
}
